import React, { useEffect, useState, useMemo } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useI18n } from "@/contexts/I18nContext";
import { formatDate } from "@/lib/format";
import { AdSlot } from "@/components/AdSlot";
import { ReadMore } from "@/components/ReadMore";
import { NotFoundState, ErrorState } from "@/components/StateViews";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUp, MessageSquare, Flag, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

const MAX_DEPTH = 6;
const ROOTS_PAGE = 20; // top-level replies shown at a time (each with its whole sub-thread)

function ReplyNode({ reply, children, depth, onReply, onUpvote, onReport, replyingId, setReplyingId, replyText, setReplyText, submitReply, user }) {
  const isReplying = replyingId === reply.id;
  const canNest = depth < MAX_DEPTH;
  const childCount = children?.props?.children?.length || (Array.isArray(children) ? children.length : 0);
  const [collapsed, setCollapsed] = React.useState(false);
  const { t, lang } = useI18n();
  const hasMany = childCount >= 3;
  return (
    <div className="border-l-2 border-border pl-4 py-2 transition-colors duration-200 hover:border-primary/40" data-testid={`reply-${reply.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
          {reply.user_avatar ? <img src={reply.user_avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-xs">{reply.user_name?.[0] || "U"}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{reply.user_name}</span> <span aria-hidden="true">·</span> <time dateTime={reply.created_at}>{formatDate(reply.created_at, lang)}</time>
          </div>
          <p className="text-sm whitespace-pre-wrap font-body-hi mt-1">{reply.body}</p>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => onUpvote(reply.id)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" data-testid={`upvote-reply-${reply.id}`}>
              <ArrowUp size={11} /> {reply.upvote_count || 0}
            </button>
            {canNest && (
              <button onClick={() => { setReplyingId(isReplying ? null : reply.id); setReplyText(""); }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" data-testid={`reply-to-${reply.id}`}>
                <CornerDownRight size={11} aria-hidden="true" /> {t("reply")}
              </button>
            )}
            <button onClick={() => onReport(reply.id)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive ml-auto" data-testid={`report-reply-${reply.id}`} aria-label={t("report")}>
              <Flag size={10} />
            </button>
          </div>
          {isReplying && user && (
            <div className="mt-2 space-y-2">
              <Textarea rows={2} value={replyText} onChange={e => setReplyText(e.target.value)} placeholder={t("write_reply")} aria-label={t("write_reply")} data-testid={`nested-reply-input-${reply.id}`} />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => submitReply(reply.id)} data-testid={`nested-reply-submit-${reply.id}`}>{t("post_reply")}</Button>
                <Button size="sm" variant="outline" onClick={() => setReplyingId(null)}>{t("cancel")}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {children && children.length > 0 && (
        <div className="mt-2 ml-4 space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
          {hasMany && (
            <button onClick={() => setCollapsed(c => !c)} className="text-[11px] text-primary hover:underline" data-testid={`collapse-${reply.id}`}>
              {collapsed ? t("show_replies").replace("{n}", childCount) : t("collapse_replies")}
            </button>
          )}
          {!collapsed && children}
        </div>
      )}
    </div>
  );
}

export default function DiscussionDetail() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [shownRoots, setShownRoots] = useState(ROOTS_PAGE);
  const { threadId } = useParams();
  const { status, data: thread, reload: load } = useFetch(`/threads/${encodeURIComponent(threadId)}`);
  usePageMeta({ title: thread?.title || (status === "notfound" ? t("not_found_title") : undefined), description: thread?.body,
    type: "article", noindex: status === "notfound" || status === "error" });
  const [reply, setReply] = useState("");
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");

  // Poll for new replies every 5s (real-time-lite; avoids WS complexity through
  // ingress) - only while the thread exists and the tab is visible.
  useEffect(() => {
    if (status !== "ok") return undefined;
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(); }, 5000);
    return () => clearInterval(iv);
  }, [status, load]);

  // Build tree
  const tree = useMemo(() => {
    if (!thread?.replies) return [];
    const byId = {}; const roots = [];
    thread.replies.forEach(r => { byId[r.id] = { ...r, children: [] }; });
    thread.replies.forEach(r => {
      if (r.parent_reply_id && byId[r.parent_reply_id]) byId[r.parent_reply_id].children.push(byId[r.id]);
      else roots.push(byId[r.id]);
    });
    return roots;
  }, [thread]);

  if (status === "notfound") return <NotFoundState />;
  if (status === "error" && !thread) return <ErrorState onRetry={load} />;
  if (!thread) return <div className="max-w-3xl mx-auto py-16"><div className="skel h-32" /></div>;

  const submitTop = async () => {
    if (!user) { toast.error(t("login_required_short")); return; }
    if (!reply.trim()) return;
    try { await api.post("/replies", { thread_id: threadId, body: reply }); setReply(""); load(); }
    catch { toast.error(t("error_title")); }
  };
  const submitReply = async (parentId) => {
    if (!replyText.trim()) return;
    try {
      await api.post("/replies", { thread_id: threadId, parent_reply_id: parentId, body: replyText });
      setReplyingId(null); setReplyText(""); load();
    } catch { toast.error(t("error_title")); }
  };
  // Every action reports failure instead of leaving an unhandled rejection.
  const act = async (fn, okMessage) => {
    try { await fn(); if (okMessage) toast.success(okMessage); load(); }
    catch { toast.error(t("error_title")); }
  };
  const upvote = () => user ? act(() => api.post(`/threads/${threadId}/upvote`)) : toast.error(t("login_required_short"));
  const upvoteReply = (rid) => user ? act(() => api.post(`/replies/${rid}/upvote`)) : toast.error(t("login_required_short"));
  const reportThread = () => user ? act(() => api.post(`/threads/${threadId}/report`), t("reported")) : toast.error(t("login_required_short"));
  const reportReply = (rid) => user ? act(() => api.post(`/replies/${rid}/report`), t("reported")) : toast.error(t("login_required_short"));

  const renderTree = (nodes, depth = 0) => nodes.map(n => (
    <ReplyNode
      key={n.id} reply={n} depth={depth}
      onUpvote={upvoteReply} onReport={reportReply}
      replyingId={replyingId} setReplyingId={setReplyingId}
      replyText={replyText} setReplyText={setReplyText}
      submitReply={submitReply} user={user}
    >
      {n.children.length > 0 ? renderTree(n.children, depth + 1) : null}
    </ReplyNode>
  ));

  const target = thread.linked_target;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-in fade-in duration-500">
      <div className="bg-card border border-border rounded-md p-6 space-y-3" data-testid="thread-detail">
        {target && (
          <Link to={`/${thread.linked_target_type}s/${target.slug}`} className="inline-block text-sm text-primary hover:underline" data-testid="thread-linked-target">
            {t(`kind_${thread.linked_target_type}`)}: {target.title_hi || target.name_hi || target.name}
          </Link>
        )}
        <h1 className="text-2xl sm:text-3xl font-serif-hi" data-testid="thread-title">{thread.title}</h1>
        <p className="text-sm text-muted-foreground">{thread.user_name} <span aria-hidden="true">·</span> <time dateTime={thread.created_at}>{formatDate(thread.created_at, lang)}</time></p>
        <ReadMore text={thread.body} lines={10} testid="thread-body" />
        <div className="flex items-center gap-3 pt-2 border-t border-border/40">
          <button onClick={upvote} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="upvote-thread" aria-label={t("upvote")}><ArrowUp size={14} aria-hidden="true" />{thread.upvote_count || 0}</button>
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground"><MessageSquare size={14} />{thread.reply_count || 0}</span>
          <button onClick={reportThread} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive ml-auto" data-testid="report-thread"><Flag size={11} aria-hidden="true" /> {t("report")}</button>
        </div>
      </div>

      {thread.body && <AdSlot placement="thread-after-post" className="mt-8" />}

      <div className="mt-8">
        <h2 className="mb-3 text-[1.4rem]">{t("replies_count").replace("{n}", thread.reply_count || 0)}</h2>
        {user ? (
          <div className="space-y-2 mb-6">
            <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder={t("write_reply")} aria-label={t("write_reply")} rows={3} data-testid="reply-input" />
            <Button onClick={submitTop} data-testid="submit-reply-btn">{t("post_reply")}</Button>
          </div>
        ) : <p className="mb-6 text-muted-foreground">{t("login_to_reply")}</p>}

        <div className="space-y-3" data-testid="replies-list">
          {tree.length === 0 && <p className="text-muted-foreground">{t("no_replies")}</p>}
          {renderTree(tree.slice(0, shownRoots))}
        </div>
        {tree.length > shownRoots && (
          <button type="button" onClick={() => setShownRoots((n) => n + ROOTS_PAGE)} data-testid="more-replies"
            className="mt-4 w-full rounded-[var(--radius)] border border-border py-2.5 hover:border-primary/50 hover:text-primary">
            {t("more_replies").replace("{n}", tree.length - shownRoots)}
          </button>
        )}
      </div>
    </div>
  );
}
